<template>
    <v-container fluid class="ue-page">
        <v-breadcrumbs :items="breadcrumbsItems" class="pa-0 mb-2" large></v-breadcrumbs>

        <v-overlay :value="isLoading" absolute opacity="0.1">
            <v-progress-circular indeterminate size="48" color="primary"></v-progress-circular>
        </v-overlay>

        <v-card v-if="loadFailed" outlined class="pa-6 text-center">
            <v-icon size="48" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <div class="mt-3 mb-4 grey--text text--darken-1">
                {{ $t('unit.edit.load_failed') }}
            </div>
            <v-btn color="primary" outlined class="normal-case mr-2" @click="load">
                {{ $t('unit.list.reload') }}
            </v-btn>
            <v-btn text class="normal-case" @click="backToList">{{ $t('unit.quest.back') }}</v-btn>
        </v-card>

        <template v-else-if="!isLoading">
            <div class="ue-head">
                <h1 class="ue-h1" :class="{ 'is-empty': !name }">
                    {{ name || $t('unit.edit.unnamed') }}
                </h1>
                <span class="ue-nth">{{ $t('unit.edit.nth', { n: unit }) }}</span>
                <span class="ue-spacer"></span>
                <button type="button" class="ue-nav" :disabled="!prevUnit"
                    @click="goSibling(prevUnit)">{{ $t('unit.edit.prev') }}</button>
                <button type="button" class="ue-nav" :disabled="!nextUnit"
                    @click="goSibling(nextUnit)">{{ $t('unit.edit.next') }}</button>
            </div>

            <!-- 關卡設定 -->
            <div class="card">
                <div class="card-head">
                    <span class="card-title">{{ $t('unit.edit.settings') }}</span>
                    <span class="ue-spacer"></span>
                    <span v-if="isDirty" class="ue-dirty">{{ $t('unit.edit.unsaved') }}</span>
                    <v-btn color="primary" depressed small class="normal-case ml-3"
                        :disabled="!isDirty" :loading="isSaving" @click="save">
                        {{ $t('unit.edit.save') }}
                    </v-btn>
                </div>

                <div class="set-body" data-rwd="set">
                    <div>
                        <div class="lbl">{{ $t('unit.edit.name_label') }}</div>
                        <input class="in" type="text" v-model="name"
                            :maxlength="UNIT_NAME_MAX"
                            :placeholder="$t('unit.edit.name_ph')">
                        <div class="cnt">{{ name.length }} / {{ UNIT_NAME_MAX }}</div>
                        <div class="note">{{ $t('unit.edit.name_note') }}</div>

                        <!-- 自由撰寫（單元層的設定，與題目的 mode 無關，兩者可以並存）。
                             ⚠ 這兩個值本來就存在 `template.units[i]`（22 本範本在用），
                             以前只能手動改資料庫——這裡是它們第一個介面入口。 -->
                        <div class="fc">
                            <!-- ⚠⚠ **有題目時不能打開**（2026-09-10 決定：整關統一）。
                                 條件是 `!freeCreation && 有題目`——**已經開著的不能鎖住**，
                                 否則 stage 上 18 個既有的混合關卡連關都關不掉。 -->
                            <label class="fc-row" :class="{ 'fc-row--off': freeLocked }">
                                <input type="checkbox" v-model="freeCreation"
                                    :disabled="freeLocked">
                                <span class="fc-label">{{ $t('unit.edit.free_creation') }}</span>
                            </label>
                            <div class="note">
                                {{ freeLocked
                                    ? $t('unit.edit.free_creation_locked',
                                         { q: questList.length })
                                    : $t('unit.edit.free_creation_note') }}
                            </div>

                            <template v-if="freeCreation">
                                <div class="lbl mt-2">{{ $t('unit.edit.free_count_label') }}</div>
                                <input class="in in--num" type="number" v-model.number="freeCount"
                                    :min="FREE_COUNT_MIN" :max="FREE_COUNT_MAX">
                                <!-- ⚠⚠ **這一句是重點。** 老師設的是「總篇數」，但他想知道的是
                                     「學生還能寫幾篇」——老師出的題目也佔名額（學生端的條件是
                                     `chapter.quests.length >= count`，而那個 quests 含範本題目）。
                                     不算給他看的話，設 3 卻只能寫 2 篇會被當成 bug。 -->
                                <!-- ⚠ 正常情況（整關統一）沒有題目，所以不必講減法。
                                     只有既有的混合關卡才需要那一句。 -->
                                <div class="note">
                                    {{ questList.length > 0
                                        ? $t('unit.edit.free_count_note', {
                                            q: questList.length, n: freeRemaining })
                                        : $t('unit.edit.free_count_note_plain', {
                                            n: freeRemaining }) }}
                                </div>
                                <!-- 題目數已經超過上限：學生一篇都寫不了。
                                     只有既有的混合關卡會走到這裡——新的自由撰寫關卡不能有題目。
                                     ⚠ 這是**警告而不是擋存檔**：那些關卡是既有資料，擋住存檔
                                     會讓老師連改個名稱都做不到。
                                     ⚠ 不要改成「後端也會擋」——後端原本擋「上限小於題目數」，
                                     2026-09-10 改成整關統一後那條規則已經拿掉了。 -->
                                <div v-if="freeCount < questList.length" class="fc-warn">
                                    {{ $t('unit.edit.free_count_over', {
                                        q: questList.length, max: freeCount,
                                    }) }}
                                </div>
                            </template>
                        </div>

                        <!--
                            設計稿的兩個畫面都沒有刪除關卡的入口，但這個功能不能消失，
                            所以放在關卡設定卡的最下面（2026-08-11 決定）。
                        -->
                        <button type="button" class="btn-danger" @click="deleteDialog = true">
                            {{ $t('unit.edit.delete_unit') }}
                        </button>
                    </div>

                    <!-- 學生看到的樣子：標題就是關卡名稱，每一列是一題 -->
                    <div>
                        <div class="lbl">{{ $t('unit.edit.preview') }}</div>
                        <div class="pv-frame">
                            <div class="pv-win">
                                <div class="pv-title" :class="{ 'is-empty': !name }">
                                    {{ name || $t('unit.edit.unnamed') }}
                                </div>
                                <div class="pv-rule"></div>
                                <div v-if="questList.length === 0" class="pv-empty">
                                    {{ $t('unit.edit.preview_empty') }}
                                </div>
                                <div v-for="quest in questList" :key="quest.quest_id"
                                    class="pv-row">
                                    <img class="pv-thumb" :src="thumbOf(quest)" alt="">
                                    <span class="pv-name" :class="{ 'is-empty': !quest.quest_title }">
                                        {{ quest.quest_title || $t('unit.edit.unnamed') }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="note">{{ $t('unit.edit.preview_note') }}</div>
                    </div>
                </div>
            </div>

            <!-- 題目 -->
            <div class="card mt-4">
                <div class="card-head">
                    <span class="card-title">{{ $t('unit.edit.quests') }}</span>
                </div>

                <div v-if="questList.length === 0" class="q-empty">
                    {{ $t('unit.edit.empty') }}
                </div>

                <!--
                    `animation` 讓其他列用滑的讓位，不是瞬間跳位；
                    `delay` + `delayOnTouchOnly` 與關卡頁一致：滑鼠立即拖，
                    觸控要按住 150ms，避免捲動頁面時誤觸。
                    ⚠ `disabled` 只吃 isBusy，**不吃排序中的狀態**——排序是放下才送出，
                    若連拖曳一起鎖住，送出的那半秒會拖不動，感覺就是卡。
                -->
                <draggable v-model="questList"
                    :animation="150" :delay="150" :delay-on-touch-only="true"
                    ghost-class="q-row--ghost" chosen-class="q-row--chosen"
                    handle=".q-grip" :disabled="isBusy"
                    @end="onQuestDragEnd">
                    <div v-for="(quest, index) in questList" :key="quest.quest_id" class="q-row">
                        <span class="q-grip">⠿</span>
                        <span class="q-no">{{ index + 1 }}</span>
                        <!-- ⚠ **`cover_url` 優先於 `thumbnail_url`**（規格 §4.3）。
                             只讀 thumbnail_url 的話，從素材庫選的封面在這裡不會顯示
                             ——那是 F4 之後的新路徑。 -->
                        <img v-if="coverOf(quest)" class="q-thumb"
                            :src="coverOf(quest)" alt="">
                        <span v-else class="q-thumb q-thumb--none">
                            {{ $t('unit.edit.no_thumb') }}
                        </span>
                        <div class="q-body">
                            <div class="q-name" :class="{ 'is-empty': !quest.quest_title }">
                                {{ quest.quest_title || $t('unit.edit.unnamed') }}
                            </div>
                            <!-- ⚠ 警示要**依模式**：翻卡牌／找不同／講顏色根本不需要畫格
                                 （規格 §7.2），影片／PDF 看「看完要不要寫」。
                                 無條件標紅會把正常的 PDF 題目標成有問題。 -->
                            <div class="q-meta" :class="{ warn: needsFrames(quest) }">
                                {{ questMeta(quest) }}
                            </div>
                        </div>
                        <button type="button" class="q-act q-act--edit"
                            :disabled="isBusy" @click="openQuest(quest)">
                            {{ $t('unit.edit.edit') }}
                        </button>
                        <button type="button" class="q-act q-act--del"
                            :disabled="isBusy" @click="askDeleteQuest(quest)">
                            {{ $t('unit.edit.delete') }}
                        </button>
                    </div>
                </draggable>

                <!-- 自由撰寫的佔位列。⚠ **一定要放在 draggable 外面**：它不是一道題目，
                     不能被拖曳，也不該進入排序送出的清單。
                     ⚠ 放在最後是為了與學生端一致——學生端的「再寫一篇」佔位是 append 在
                     questList 的最後（`store/app.js`），老師在這裡看到的順序要一樣。 -->
                <div v-if="freeCreation" class="q-row q-row--free">
                    <!-- ⚠ 把手的位置要留白而不是省略：題目列的第一個元素是 32px 的
                         `.q-grip`（還帶負 margin 吃掉 padding），少了它序號會左移
                         一格，兩種列的數字就對不齊了。這一格刻意沒有圖示與 cursor。 -->
                    <span class="q-grip-space" aria-hidden="true"></span>
                    <span class="q-no q-no--free">{{ freeRange }}</span>
                    <!-- 斜線灰底的空框：它不是一張真的縮圖，也不該放文字
                         （文字會讓它看起來像一道有標題的題目）。 -->
                    <span class="q-thumb q-thumb--free" aria-hidden="true"></span>
                    <div class="q-body">
                        <div class="q-name q-name--free">
                            {{ $t('unit.edit.free_creation_row') }}
                        </div>
                        <div class="q-meta">
                            {{ questList.length > 0
                                ? $t('unit.edit.free_creation_row_note',
                                     { x: questList.length })
                                : $t('unit.edit.free_creation_row_note_empty') }}
                        </div>
                    </div>
                    <!-- 剩餘篇數。⚠ 這個數字是「學生還能寫幾篇」而不是總上限——
                         老師出的題目已經佔掉名額。
                         「由學生自行決定」是在說**實際寫幾篇**由學生決定，
                         老師設的只是天花板。 -->
                    <div class="q-free-right">
                        <span class="q-free-max">
                            {{ $t('unit.edit.free_remaining', { n: freeRemaining }) }}
                        </span>
                    </div>
                </div>
            </div>

            <!--
                新增入口放在卡片**外面**，跟關卡列表的「＋ 新增關卡」一致。
                放在卡片裡的話，它的 margin-bottom 會跟沒有下內距的卡片下緣合併、
                跑到卡片外，看起來就是「下方沒有間距」。
            -->
            <!-- ⚠⚠ **自由撰寫的關卡不給新增題目**（2026-09-10 決定：整關統一）。
                 這是兩個入口之一，另一個是上面「有題目時開關 disabled」。
                 兩邊都要擋，只擋一邊的話換另一條路就能造出混合的關卡。
                 ⚠ 後端也擋（`saveUnitQuest` 會回 403）——擋在 UI 是體驗，擋在後端才是規則。 -->
            <div v-if="freeCreation" class="q-add q-add--full">
                {{ $t('unit.edit.add_blocked_free') }}
            </div>
            <button v-else-if="questList.length < MAX_QUEST_PER_UNIT" type="button" class="q-add"
                :disabled="isBusy" @click="askQuestMode">
                {{ $t('unit.edit.add', { n: MAX_QUEST_PER_UNIT - questList.length }) }}
            </button>
            <div v-else class="q-add q-add--full">{{ $t('unit.edit.full') }}</div>
        </template>

        <v-dialog v-model="deleteDialog" persistent width="480">
            <v-card>
                <v-card-title class="text-h6">
                    {{ name ? $t('unit.list.delete_title_named', { name: name })
                        : $t('unit.list.delete_title') }}
                </v-card-title>
                <v-card-text class="pb-2">
                    <div class="grey--text text--darken-3">
                        {{ questList.length > 0
                            ? $t('unit.list.delete_message_counts', {
                                q: questList.length, f: frameTotal, s: sampleTotal })
                            : $t('unit.list.delete_message_empty') }}
                    </div>
                    <v-alert color="error" text dense class="mt-3 mb-0 text-body-2">
                        {{ $t('unit.list.delete_warn') }}
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" :disabled="isBusy"
                        @click="deleteDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" class="normal-case" :loading="isBusy"
                        @click="confirmDeleteUnit">
                        {{ $t('unit.list.delete_unit') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="questDeleteDialog" persistent width="440">
            <v-card>
                <v-card-title class="text-h6">{{ $t('unit.edit.q_delete_title') }}</v-card-title>
                <v-card-text>
                    {{ deleteTarget.frame_count > 0
                        ? $t('unit.edit.q_delete_message', {
                            f: deleteTarget.frame_count, s: deleteTarget.sample_total })
                        : $t('unit.edit.q_delete_empty') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" :disabled="isBusy"
                        @click="questDeleteDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" class="normal-case" :loading="isBusy"
                        @click="confirmDeleteQuest">
                        {{ $t('button.delete') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="leaveDialog" persistent width="480">
            <v-card>
                <v-card-title class="text-h6">{{ $t('unit.edit.leave_title') }}</v-card-title>
                <v-card-text>{{ $t('unit.edit.leave_message') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="resolveLeave(false)">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" text class="normal-case" @click="resolveLeave(true)">
                        {{ $t('unit.edit.leave_confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ⚠ 建立前一定要選模式，理由見 askQuestMode -->
        <QuestModeDialog v-model="modeDialog" :busy="isBusy" @confirm="addQuest" />

        <SnackMsg ref="snack" />
    </v-container>
</template>

<script>
import draggable from 'vuedraggable';
import { templateUnitServices } from '@/services/templateUnit.js';

import { diaryServices } from '@/services/diary.js';
import SnackMsg from '@/components/console/diary/component/SnackMsg';
import QuestModeDialog from '@/components/console/diary/unit/QuestModeDialog';
import {
    minFrameCount, framesAreAuto, displayMode, composesFrameFromTypes, MODE_VIDEO, MODE_PDF,
} from '@/components/console/diary/unit/questModeUtils';
import {
    MAX_QUEST_PER_UNIT, UNIT_NAME_MAX, DEFAULT_QUEST_THUMBNAIL,
} from '@/components/console/diary/unit/unitConstants';

/// 自由撰寫的總篇數上限。
///
/// ⚠⚠ **直接用 `MAX_QUEST_PER_UNIT`，不要另外寫一個 4**（2026-09-10 從 10 改成
/// 「跟題目數相同」）。`count` 是那一關的**總篇數**（含老師出的題目），所以它的
/// 天花板本來就是一關能有幾題。後端也是同一個關係（`FREE_COUNT_MAX`）。
///
/// ⚠ 前端擋是為了體驗，後端擋才是規則。
const FREE_COUNT_MIN = 1;
const FREE_COUNT_MAX = MAX_QUEST_PER_UNIT;
/// 老師沒設過時的建議值。⚠ 學生端的預設也是 3（`count || 3`），所以這個數字
/// 不是憑空選的——它讓「預填」與「不設定的實際行為」一致。
const FREE_COUNT_DEFAULT = 3;


/**
 * 關卡頁（設計稿 1b）：關卡設定 + 該關的題目清單。
 *
 * 2026-08-11 由 modal 改成獨立頁。路由是 `/diarySample/:id/units/:unit`——
 * ⚠ 題目層因此從 `/units/:questId` 搬到 `/quests/:questId`，兩者都是數字，
 * 同一條路徑會撞在一起。
 *
 * 三種寫入各走各的 API：關卡名稱按按鈕才送，題目的新增／刪除／排序立即送出。
 */
export default {
    name: 'UnitEdit',
    components: { draggable, SnackMsg, QuestModeDialog },
    data() {
        return {
            FREE_COUNT_MIN,
            FREE_COUNT_MAX,
            homeworkId: '',
            modeDialog: false,
            unit: 0,
            homeworkName: '',
            breadcrumbsItems: [],
            isLoading: true,
            loadFailed: false,
            isSaving: false,
            isBusy: false,
            /// 與 isBusy 分開：排序送出時**不鎖拖曳**，否則每放下一次就頓一下
            isReordering: false,
            name: '',
            baselineName: '',
            /// 自由撰寫。⚠ `freeCount` 一律是數字（載入時 null 會補成建議值 3），
            /// 但 `baselineFreeCount` 記的是**後端原本的值**（可能是 null）——
            /// 存檔時要用它判斷「老師有沒有真的動過這個數字」。
            freeCreation: false,
            freeCount: FREE_COUNT_DEFAULT,
            baselineFreeCreation: false,
            baselineFreeCount: null,
            questList: [],
            frameTotal: 0,
            sampleTotal: 0,
            unitCount: 0,
            deleteDialog: false,
            questDeleteDialog: false,
            deleteTarget: {},
            leaveDialog: false,
            leaveResolve: null,
            MAX_QUEST_PER_UNIT,
            UNIT_NAME_MAX,
        };
    },
    computed: {
        /**
         * 開關要不要鎖住。
         *
         * ⚠ **只鎖「還沒開、但已經有題目」的情況。** 已經開著的一律可以關掉——
         * stage 上有 18 個既有的混合關卡（自由撰寫 + 範本題目），需求方決定原樣保留，
         * 無條件鎖住的話那些關卡連關都關不掉。
         */
        freeLocked() {
            return !this.freeCreation && this.questList.length > 0;
        },
        /** 學生還能寫幾篇。⚠ 既有的混合關卡裡，老師出的題目也佔名額。 */
        freeRemaining() {
            return Math.max(0, this.freeCount - this.questList.length);
        },
        /**
         * 學生那幾篇會是第幾題到第幾題。
         * ⚠ 剩餘為 0 時回破折號而不是一段反向的範圍（例如「5–4」）。
         */
        freeRange() {
            if (this.freeRemaining <= 0) {
                return '—';
            }
            const from = this.questList.length + 1;
            return from === this.freeCount ? String(from) : `${from}–${this.freeCount}`;
        },
        isDirty() {
            if (this.name !== this.baselineName) {
                return true;
            }
            if (this.freeCreation !== this.baselineFreeCreation) {
                return true;
            }
            /// ⚠ 只有在**打開**的狀態下才比數字：關掉時數字不會送出去
            /// （後端保留舊值），所以那時改數字不算變更。
            ///
            /// ⚠⚠ **要跟「預填之後的值」比，不是跟後端原本的 null 比。**
            /// 有一種資料是 `free_creation: true` 但沒有 `count`（stage 上有一筆，
            /// hw 1925）——那時 baseline 是 null 而輸入框預填 3，直接比就永遠不相等，
            /// 於是頁面一打開就顯示「有未儲存的變更」、存檔鈕亮起，而老師什麼都沒動。
            const baseline = this.baselineFreeCount === null
                ? FREE_COUNT_DEFAULT : this.baselineFreeCount;
            return this.freeCreation && this.freeCount !== baseline;
        },
        prevUnit() {
            return this.unit > 1 ? this.unit - 1 : 0;
        },
        nextUnit() {
            return this.unit < this.unitCount ? this.unit + 1 : 0;
        },
    },
    watch: {
        /// 上一關／下一關是同一個元件換參數，`created` 不會再跑
        '$route.params.unit': function reload() {
            this.unit = Number(this.$route.params.unit);
            this.load();
        },
    },
    async created() {
        this.homeworkId = this.$route.params.id;
        this.unit = Number(this.$route.params.unit);
        await this.load();
    },
    beforeRouteLeave(to, from, next) {
        if (!this.isDirty) {
            next();
            return;
        }
        this.leaveResolve = next;
        this.leaveDialog = true;
    },
    methods: {
        async load() {
            this.isLoading = true;
            this.loadFailed = false;
            try {
                const [detailRes, listRes] = await Promise.all([
                    diaryServices.getHomeworkDetail(this.homeworkId),
                    templateUnitServices.getUnitList(this.homeworkId),
                ]);
                this.homeworkName = detailRes?.data?.name || '';
                const unitList = listRes?.data?.units || [];
                this.unitCount = unitList.length;
                const found = unitList.find((item) => item.unit === this.unit);
                if (!found) {
                    this.loadFailed = true;
                    return;
                }
                this.name = found.name || '';
                this.baselineName = this.name;
                this.freeCreation = found.free_creation === true;
                /// ⚠ 後端刻意回 `null` 而不是 3（見 `readFreeCount`）：那樣才分得出
                /// 「老師沒設過」與「他刻意設成 3」。UI 預填建議值，但 baseline 記 null，
                /// 所以只有他真的改動時才算 dirty。
                this.baselineFreeCreation = this.freeCreation;
                this.baselineFreeCount = typeof found.free_count === 'number'
                    ? found.free_count : null;
                this.freeCount = this.baselineFreeCount === null
                    ? FREE_COUNT_DEFAULT : this.baselineFreeCount;
                this.questList = (found.quests || []).slice();
                this.frameTotal = found.frame_total || 0;
                this.sampleTotal = found.sample_total || 0;
                this.buildBreadcrumbs();
            } catch (err) {
                this.loadFailed = true;
            } finally {
                this.isLoading = false;
            }
        },

        buildBreadcrumbs() {
            this.breadcrumbsItems = [
                { text: this.$t('main_menu.quest'), disabled: false, href: '/diarySample' },
                {
                    text: this.homeworkName,
                    disabled: false,
                    /// 範本內容主頁（舊版與關卡式共用），不是 /diarySample/:id
                    href: `/diary/homework/${this.homeworkId}/quest`,
                },
                {
                    text: this.$t('unit.list.page_title'),
                    disabled: false,
                    href: `/diarySample/${this.homeworkId}/units`,
                },
                {
                    text: this.name
                        ? this.$t('unit.edit.breadcrumb', { n: this.unit, name: this.name })
                        : this.$t('unit.edit.breadcrumb_unnamed', { n: this.unit }),
                    disabled: true,
                },
            ];
        },

        /**
         * 這一題實際會顯示的封面。順序與學生端的解析一致（規格 §4.3）：
         * `cover_url`（素材庫）→ `thumbnail_url`（舊路徑）→ 沒有。
         */
        coverOf(quest) {
            return quest.cover_url || quest.thumbnail_url || '';
        },

        /// 學生端在沒有縮圖時會顯示預設圖，所以預覽也要顯示同一張
        thumbOf(quest) {
            return this.coverOf(quest) || DEFAULT_QUEST_THUMBNAIL;
        },

        /**
         * 這一題**缺畫格**嗎（決定要不要標紅）。
         *
         * ⚠ **不是「frame_count === 0」。** 四種遊戲裡有三種根本不需要畫格，
         * 影片／PDF 則看「看完要不要寫」（規格 §7.2）。無條件標紅會把一個設定完整的
         * PDF 題目標成有問題，而老師沒有任何方式可以消掉那個紅字。
         *
         * ⚠ 規則來自 `questModeUtils.minFrameCount`，與後端的
         * `QuestModeSettingsValidator.minFrameCountForMode` 同一份定義。
         */
        needsFrames(quest) {
            /// 合成畫格的題目缺了那一格學生端就合成不了，照樣標成警告色（只是文案不同，見 questMeta）
            if (this.isComposedFrame(quest)) {
                return quest.frame_count === 0;
            }
            return quest.frame_count < minFrameCount(quest.mode, quest.require_writing);
        },

        /// 日記畫格由系統合成（見 questModeUtils.composesFrameFromTypes）。列表只有摘要的 `types`。
        isComposedFrame(quest) {
            return composesFrameFromTypes((quest.video_quest && quest.video_quest.types) || []);
        },

        /**
         * ⚠ **只講畫格數，不講範例數**。範例是分散在各個畫格底下的，型別還不一樣
         * （事件 3 個 + 行動 4 個），加總成「7 個範例」對老師沒有意義。
         * 刪除確認那邊仍然會數範例——那裡要講的是「會消失多少東西」，語意不同。
         */
        /**
         * 模式的顯示名稱。**不認識的 mode 回空字串**，呼叫端自己決定要退回什麼。
         *
         * ⚠ 不認識的 mode 一定會出現：B7 之前建的題目沒有 `mode`，而未來還會加新的
         * （規格 §2.5 的容忍規則）。直接 `$t()` 會把 raw key 印在畫面上。
         */
        modeLabel(quest) {
            /// ⚠ 用 `displayMode`：只看影片的舊題目沒有 mode，但要顯示成「看影片」
            const key = `unit.mode.mode_${displayMode(quest)}`;
            return this.$te(key) ? this.$t(key) : '';
        },

        questMeta(quest) {
            /// 合成畫格的題目：畫格不是老師編的，講「1 個畫格」是雜訊，講「還沒有畫格」會叫他去補一個他補不了的東西
            if (this.isComposedFrame(quest)) {
                return quest.frame_count === 0
                    ? this.$t('unit.edit.meta_composed_missing')
                    : this.$t('unit.edit.meta_composed');
            }
            /// 缺畫格永遠優先講——那是真的有問題（含找單字：自動建的那一格被刪掉了）
            if (this.needsFrames(quest)) {
                return this.$t('unit.edit.meta_no_frame');
            }
            /// 講模式名稱而不是畫格數的兩種情況：
            /// ① **畫格是自動建的**（找單字，2026-09-04）。它的下限是 1，所以永遠會有
            ///    一格，但「1 個畫格」對老師是雜訊——那一格是「學生玩完自動存下來的紀錄」，
            ///    不是他編的分鏡。他需要知道的是「這一題是找單字」。
            ///    ⚠ 代價：老師自己加了第 2–4 格時這裡也不顯示數量。可以接受——
            ///    自動建的那一格讓數字本來就對不上他的作業量（加了 2 格會顯示 3）。
            /// ② **0 格但這個模式不需要畫格**（三種遊戲、不用寫的影片／PDF）。
            ///    這時講「還沒有畫格」會讓老師去找一個不存在的問題。
            if (framesAreAuto(quest.mode) || quest.frame_count === 0) {
                /// ⚠ 不認識的 mode 退回既有文案而不是顯示 raw key（規格 §2.5）
                return this.modeLabel(quest) || this.$t('unit.edit.meta_no_frame');
            }
            /// 影片／PDF 有畫格時**兩件都講**（2026-09-04）。那兩個模式的畫格是**選填的**
            /// （看完要不要寫，`minFrameCount` 對它們取決於 `require_writing`），
            /// 所以單看「2 個畫格」分不出這是影片題還是 PDF 題——而那正是老師在列表上
            /// 最想先知道的事。
            /// ⚠ **`writing` 不併**：它的畫格就是全部內容，「看圖寫作 · 2 個畫格」是廢話。
            /// ⚠ **遊戲不併**：三種沒有畫格，找單字的那一格是自動建的（上面的分支就處理掉了）。
            if (quest.mode === MODE_VIDEO || quest.mode === MODE_PDF) {
                const label = this.modeLabel(quest);
                if (label) {
                    return this.$t('unit.edit.meta_mode_frame', {
                        mode: label, n: quest.frame_count,
                    });
                }
            }
            return this.$t('unit.edit.meta_frame', { n: quest.frame_count });
        },

        async save() {
            this.isSaving = true;
            try {
                /// description 一律回送空字串——設計稿把關卡說明列為 out of scope，
                /// 但後端的既有值不會被這裡清掉（saveUnit 只覆寫 name / description
                /// 兩個 key，而這個編輯器本來就沒有說明欄位可以編）
                /// ⚠ 關掉時 `count` 送 null（＝不動那個 key），數字保留給下次打開
                /// （2026-09-10 決定）。打開時一律送數字，把隱含的預設 3 變成明確的值。
                await templateUnitServices.saveUnit(
                    this.homeworkId, this.unit, this.name, '',
                    this.freeCreation, this.freeCreation ? this.freeCount : null);
                this.baselineName = this.name;
                this.baselineFreeCreation = this.freeCreation;
                if (this.freeCreation) {
                    this.baselineFreeCount = this.freeCount;
                }
                this.buildBreadcrumbs();
                this.$refs.snack?.showAlertMsg('success', this.$t('unit.edit.saved'));
            } catch (err) {
                this.toastError(this.$t('unit.edit.save_failed'));
            } finally {
                this.isSaving = false;
            }
        },

        goSibling(unit) {
            if (!unit) {
                return;
            }
            this.$router.push(`/diarySample/${this.homeworkId}/units/${unit}`);
        },

        backToList() {
            this.$router.push(`/diarySample/${this.homeworkId}/units`);
        },

        openQuest(quest) {
            this.$router.push(`/diarySample/${this.homeworkId}/quests/${quest.quest_id}`);
        },

        /**
         * ⚠ 先開模式對話框，不直接建立。
         *
         * `mode` 是後端必填（B7）且**建立後不可變更**（ADR 0002）。而且**這個入口不能漏**：
         * 模式是每一題的屬性而一關最多 4 題，只有「新增關卡」帶 mode 的話，
         * 從這裡進來的第 2–4 題會沒有模式（規格 §5.1）。
         */
        askQuestMode() {
            if (this.questList.length >= MAX_QUEST_PER_UNIT || this.isBusy) {
                return;
            }
            this.modeDialog = true;
        },

        async addQuest(mode) {
            if (this.questList.length >= MAX_QUEST_PER_UNIT) {
                return;
            }
            this.isBusy = true;
            try {
                /// ⚠ **只有找單字帶 `frameTitle`**：它建立時會自動補一格（學生玩完會
                /// 自動存成一篇紀錄），先給一個預設標題老師才知道那一格是幹嘛的。
                /// 其他模式的畫格是老師自己加的，不該替他預填。
                const res = await templateUnitServices.addQuest(
                    this.homeworkId, this.unit, mode,
                    mode === 'game_wordsearch'
                        ? this.$t('unit.frame.wordsearch_title_default')
                        : null);
                this.modeDialog = false;
                /// 新增當下後端就給 quest_id，所以直接進題目層，沒有「新增模式」的分支
                this.$router.push(
                    `/diarySample/${this.homeworkId}/quests/${res?.data?.quest_id}`);
            } catch (err) {
                this.toastError(this.$t('unit.edit.add_failed'));
            } finally {
                this.isBusy = false;
            }
        },

        askDeleteQuest(quest) {
            this.deleteTarget = quest;
            this.questDeleteDialog = true;
        },

        async confirmDeleteQuest() {
            this.isBusy = true;
            try {
                await templateUnitServices.deleteQuest(
                    this.homeworkId, this.deleteTarget.quest_id);
                this.questDeleteDialog = false;
                await this.load();
            } catch (err) {
                this.toastError(this.$t('unit.edit.delete_failed'));
            } finally {
                this.isBusy = false;
            }
        },

        /** 題目重排立即送出。失敗就重載，不留下與資料庫不符的畫面。 */
        async onQuestDragEnd(event) {
            if (event.oldIndex === event.newIndex) {
                return;
            }
            this.isReordering = true;
            try {
                await templateUnitServices.reorderQuests(
                    this.homeworkId, this.unit,
                    this.questList.map((item) => item.quest_id));
            } catch (err) {
                this.toastError(this.$t('unit.edit.reorder_failed'));
                await this.load();
            } finally {
                this.isReordering = false;
            }
        },

        async confirmDeleteUnit() {
            this.isBusy = true;
            try {
                await templateUnitServices.deleteUnit(this.homeworkId, this.unit);
                this.deleteDialog = false;
                /// 刪完這一關就不存在了，而且後面的關卡會前移——回列表重新載入
                this.baselineName = this.name;
                this.$router.push(`/diarySample/${this.homeworkId}/units`);
            } catch (err) {
                this.toastError(this.$t('unit.list.delete_failed'));
            } finally {
                this.isBusy = false;
            }
        },

        resolveLeave(shouldLeave) {
            this.leaveDialog = false;
            const next = this.leaveResolve;
            this.leaveResolve = null;
            if (next) {
                next(shouldLeave ? undefined : false);
            }
        },

        toastError(msg) {
            this.$toastr.Add({
                msg: msg,
                type: 'error',
                timeout: 4000,
                clickClose: true,
                position: 'toast-top-right',
                classNames: ['animated', 'bounceInRight'],
            });
        },
    },
};
</script>

<style scoped>
/* ── 自由撰寫（2026-09-10） ───────────────────────────────── */
.fc {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #eceff1;
}

.fc-row {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

/* 鎖住時要看得出來，否則老師會一直點那個沒反應的勾選框 */
.fc-row--off {
    cursor: not-allowed;
    opacity: .55;
}

.fc-label {
    font-size: 14px;
    color: #37474f;
}

/* 數字欄不需要跟文字欄一樣寬——上限只有兩位數 */
.in--num {
    max-width: 96px;
}

.fc-warn {
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.6;
    color: #c62828;
}

/* 自由撰寫的佔位列。
   ⚠ 內距與分隔線**沿用 `.q-row`**，只換底色——它是題目清單的一部分，
   不是另一個區塊。原本用黑色粗虛線外框，那讓它看起來像一個獨立的卡片。
   ⚠ **明確不可互動**：沒有 hover、沒有 cursor 變化、沒有按鈕、沒有把手。 */
.q-row--free {
    background: #fafbfc;
    cursor: default;
}

.q-row--free:hover {
    background: #fafbfc;
}

/* 把手的位置留白。數值要與 `.q-grip` 完全一致，否則兩種列的序號對不齊。 */
.q-grip-space {
    width: 32px;
    flex: 0 0 32px;
    margin: -16px 0 -16px -18px;
}

/* 序號是一段範圍（例如 2–4），比單一數字寬 */
.q-no--free {
    font-size: 13px;
    color: #90a4ae;
    white-space: nowrap;
}

/* 斜線灰底的空框：一眼看出「這裡不會有縮圖」。
   ⚠ 用 repeating-linear-gradient 而不是圖檔或字元，省一個請求也不受字型影響。 */
.q-thumb--free {
    background: repeating-linear-gradient(45deg,
        #f4f5f7 0, #f4f5f7 5px, #eceef1 5px, #eceef1 10px);
    border: 1px solid #e8eaed;
    box-sizing: border-box;
}

.q-name--free {
    color: #5f6b76;
}

.q-free-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    margin-left: auto;
}

.q-free-max {
    font-size: 12px;
    color: #90a4ae;
    white-space: nowrap;
}

/* 設計 token 見 support/關卡編輯/介面調整/README.md。方角、細陰影。 */
.ue-page {
    padding-bottom: 40px;
}

.ue-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 16px;
}

.ue-h1 {
    font-size: 26px;
    font-weight: 500;
    color: #424242;
    line-height: 1.3;
    margin: 0;
}

.ue-h1.is-empty {
    color: #9e9e9e;
}

.ue-nth {
    font-size: 14px;
    color: #9e9e9e;
}

.ue-spacer {
    flex: 1;
}

.ue-nav {
    font-size: 13px;
    color: #9e9e9e;
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
}

.ue-nav:hover:not(:disabled) {
    color: #424242;
}

.ue-nav:disabled {
    color: #e0e0e0;
    cursor: not-allowed;
}

.card {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .14);
}

.card-head {
    display: flex;
    align-items: center;
    padding: 13px 18px;
    border-bottom: 1px solid #eeeeee;
}

.card-title {
    font-size: 16px;
    font-weight: 500;
    color: #424242;
}

.ue-dirty {
    font-size: 12px;
    color: #9e9e9e;
}

.set-body {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 24px;
    padding: 14px 18px 18px;
    align-items: start;
}

.lbl {
    font-size: 12px;
    color: #757575;
    margin-bottom: 6px;
}

.in {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #bdbdbd;
    padding: 11px 13px;
    font-size: 16px;
    font-family: inherit;
    color: #212121;
    background: #fff;
    outline: none;
}

.in:focus {
    border-color: #757575;
}

.cnt {
    text-align: right;
    font-size: 11px;
    color: #bdbdbd;
    margin-top: 4px;
}

.note {
    font-size: 12px;
    color: #9e9e9e;
    line-height: 1.7;
    margin-top: 6px;
}

.btn-danger {
    margin-top: 18px;
    border: 1px solid #ef9a9a;
    color: #d32f2f;
    background: #fff;
    padding: 7px 14px;
    font-size: 13px;
    cursor: pointer;
}

/* 學生端視窗的模擬。底襯色只是為了讓白色視窗浮起來，不是產品色。 */
.pv-frame {
    background: #eaf1f4;
    border: 1px solid #e0e0e0;
    padding: 14px;
}

.pv-win {
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 3px 12px rgba(0, 0, 0, .18);
    padding: 14px 16px;
}

.pv-title {
    font-size: 17px;
    font-weight: 700;
    color: #212121;
    text-align: center;
}

.pv-title.is-empty {
    color: #bdbdbd;
}

.pv-rule {
    border-top: 1px solid #e0e0e0;
    margin: 10px 0;
}

.pv-empty {
    font-size: 12px;
    color: #9e9e9e;
    line-height: 1.7;
}

.pv-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
}

.pv-thumb {
    width: 74px;
    height: 52px;
    flex: 0 0 74px;
    object-fit: cover;
    border-radius: 3px;
    background: #f0f0f0;
}

.pv-name {
    font-size: 14px;
    font-weight: 700;
    color: #212121;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pv-name.is-empty {
    color: #bdbdbd;
    font-weight: 400;
}

.q-empty {
    font-size: 13px;
    color: #9e9e9e;
    padding: 16px 18px;
}

.q-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 18px;
    border-bottom: 1px solid #f0f0f0;
}

/* 最後一列的分隔線會貼在卡片下緣，看起來像卡片自己多一條線 */
.q-row:last-child {
    border-bottom: 0;
}

/* 抓取區要夠大。原本只有字符本身約 15px，很難抓準——關卡頁的把手是 32px 寬 */
.q-grip {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    width: 32px;
    flex: 0 0 32px;
    margin: -16px 0 -16px -18px;
    color: #bdbdbd;
    font-size: 15px;
    cursor: grab;
}

.q-grip:active {
    cursor: grabbing;
}

/* 拖曳中的佔位列：留出空間但不搶視覺 */
.q-row--ghost {
    opacity: .4;
    background: #f3f8fe;
}

.q-row--chosen {
    background: #fafafa;
}

.q-no {
    width: 30px;
    flex: 0 0 30px;
    text-align: center;
    font-size: 15px;
    color: #bdbdbd;
}

.q-thumb {
    width: 86px;
    height: 60px;
    flex: 0 0 86px;
    object-fit: cover;
    border-radius: 3px;
    background: #f0f0f0;
}

.q-thumb--none {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #d5d5d5;
    font-size: 11px;
    color: #bdbdbd;
    text-align: center;
    line-height: 1.3;
}

.q-body {
    flex: 1;
    min-width: 0;
}

.q-name {
    font-size: 16px;
    color: #424242;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.q-name.is-empty {
    color: #bdbdbd;
}

.q-meta {
    font-size: 12px;
    color: #9e9e9e;
    margin-top: 2px;
}

/* 沒有畫格是真的要補的東西，用橘色；跟「尚無題目」的中性灰不同級 */
.q-meta.warn {
    color: #e65100;
}

.q-act {
    font-size: 13px;
    background: none;
    border: none;
    cursor: pointer;
    flex: 0 0 auto;
}

.q-act--edit {
    color: #2196f3;
}

.q-act--del {
    color: #e53935;
}

.q-act:disabled {
    color: #bdbdbd;
    cursor: not-allowed;
}

.q-add {
    display: block;
    width: 100%;
    margin-top: 16px;
    border: 1px dashed #c9c9c9;
    background: #fafafa;
    color: #9e9e9e;
    font-size: 14px;
    padding: 16px;
    text-align: center;
    cursor: pointer;
}

.q-add:hover:not(:disabled) {
    border-color: #9e9e9e;
    color: #757575;
}

.q-add--full,
.q-add--full:hover {
    border-color: #e0e0e0;
    color: #bdbdbd;
    cursor: default;
}

@media (max-width: 1023px) {
    [data-rwd="set"] {
        grid-template-columns: 1fr;
    }
}
</style>
